import { setFormError, splitDataSetTest } from 'js/actions';
import ConfigureRecipeHeader from 'js/components/recipeBuilder/ConfigureRecipeHeader';
import { SchemaEditorViewModeType } from 'js/constants/schemaEditor';
import useAppDispatch from 'js/hooks/useAppDispatch';
import JSONUtils from 'js/lib/JSONUtils';
import { isArray, isNil, isPlainObject, map } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { DataSetSplitFallback, DataSetSplitModes } from './DataSetSplitDialog';
import useSplitDataSet from './useSplitDataSet';

export default function DataSetSplitTest({
  parentDataSet,
  splitterDataSet,
  selectedViewMode,
  customSamplesCode,
  showMeta,
}: {
  parentDataSet: any;
  splitterDataSet?: any;
  selectedViewMode?: string;
  customSamplesCode?: string;
  showMeta: boolean;
}) {
  const dispatch = useAppDispatch();
  const {
    mode,
    expressions,
    attr,
    values,
    fallback,
    parentDataSetSchema,
    setMode,
    setExpressions,
    setAttr,
    setValues,
    setFallback,
    fetchDataSets,
    validateExpressions,
  } = useSplitDataSet(parentDataSet, splitterDataSet);
  const formType = useSelector((state: any) => state.forms.activeType);
  const inputSamples = useSelector((state: any) => state.schemaEditor.fromSamples.fromNested);
  const [parsingErrors, setParsingErrors] = useState<Record<string, string>>({});
  const [isLoadingTestResults, setIsLoadingTestResults] = useState(false);

  useEffect(() => {
    fetchDataSets();
  }, []);

  const onTestRules = () => {
    const errors = validateExpressions();

    if (errors) {
      setParsingErrors(errors);
      return;
    }

    setIsLoadingTestResults(true);

    dispatch(
      splitDataSetTest(mode, expressions, attr, values, fallback, getInputSamples()),
    ).finally(() => {
      setIsLoadingTestResults(false);
    });
  };

  const getInputSamples = () => {
    // parse user-entered input samples (copied from
    // ConfigureRecipe._handleApplyRecipeClicked)
    let samples: any[];

    if (selectedViewMode === SchemaEditorViewModeType.CODE && !isNil(customSamplesCode)) {
      let customSamples = JSONUtils.parseJson(customSamplesCode);

      if (isPlainObject(customSamples)) {
        customSamples = [customSamples];
      } else if (!isArray(customSamples)) {
        dispatch(setFormError(formType, 'Please enter a valid JSON array.'));
        return;
      }

      samples = showMeta
        ? customSamples
        : map(customSamples, (sample, index) => ({
            rawMessage: sample,
            nexlaMetaData:
              inputSamples?.[index]?.nexlaMetaData || inputSamples?.[0]?.nexlaMetaData || {},
          }));
    } else {
      samples = inputSamples;
    }

    return samples;
  };

  return (
    <div className="configure-recipe configure-recipe-splitter">
      <ConfigureRecipeHeader
        showAsyncControl={false}
        isRecipeUnapplied={false}
        isLoading={isLoadingTestResults}
        onApplyRecipeClicked={onTestRules}
      />
      <div className="configure-recipe-body">
        <ol className="recipe-groups-list">
          <li className="recipe-group">
            <DataSetSplitModes
              mode={mode}
              parentDataSetSchema={parentDataSetSchema}
              parsingErrors={parsingErrors}
              expressions={expressions}
              attr={attr}
              values={values}
              setMode={setMode}
              setAttr={setAttr}
              setExpressions={setExpressions}
              setValues={setValues}
            />
            <DataSetSplitFallback fallback={fallback} setFallback={setFallback} />
          </li>
        </ol>
      </div>
    </div>
  );
}
